import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { LabCardComponent } from 'src/app/_components/cards/lab-card/lab-card.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { ErrorPageComponent } from '../../_components/error-page/error-page.component';
import { UnderlinedDropdownFormFieldComponent } from '../../_components/forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import { OrganisationIndexView } from 'src/app/_models/organisation';
import { OGI_ADMIN_ROLE_LIST, Role, SUPER_ADMIN_ROLE_LIST } from 'src/app/_models/role';
import { User, UserCreate } from 'src/app/_models/user';
import { AuthEventService } from 'src/app/_services/auth-event.service';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ModalService } from 'src/app/_services/modal.service';
import { OrganisationService } from 'src/app/_services/organisation.service';
import { UserService } from 'src/app/_services/user.service';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrl: './add-user.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UnderlinedFormFieldComponent,
    UnderlinedDropdownFormFieldComponent,
    ButtonComponent,
    ErrorPageComponent,
    ModalComponent,
    LabCardComponent,
    LoadingSpinnerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddUserComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly organisationService = inject(OrganisationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly authEventService = inject(AuthEventService);

  private readonly unsubscribe$ = new Subject<void>();
  private readonly allowNavigation = new Subject<boolean>();

  laboratory: Laboratory;
  orgId: number;
  currentUser: User;

  isProcessing = signal(false);
  labsLoading = signal(false);
  error = signal<ErrorObject | null>(null);
  availableLabs = signal<Laboratory[]>([]);
  selectedLabs = signal<Laboratory[]>([]);
  labSelectError = signal<string | null>(null);
  addUserSuccessMessage = signal<string | null>(null);

  firstNameField = new FormBase({
    key: 'firstName',
    label: 'First Name',
    type: 'text',
    placeholder: 'First name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  lastNameField = new FormBase({
    key: 'lastName',
    label: 'Last Name',
    type: 'text',
    placeholder: 'Last name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  emailField = new FormBase({
    key: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'Email address',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  userRoleField = new FormBase({
    key: 'role',
    label: 'Role',
    type: 'select',
    placeholder: 'Select role',
    disabled: false,
    required: true,
    value: '',
    options: [
      { key: Role.Read_Access_User, value: 'Read Access User' },
      { key: Role.Run_Access_User, value: 'Run Access User' },
      { key: Role.Edit_Access_User, value: 'Edit Access User' },
      { key: Role.Lab_Administrator, value: 'Lab Administrator' },
    ],
  });

  availableLabsField = new FormBase({
    key: 'lab',
    label: 'User Laboratories',
    type: 'select',
    placeholder: 'Select lab',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  addUserForm = this.formBuilder.group({
    [this.firstNameField.key]: ['', [Validators.required]],
    [this.lastNameField.key]: ['', [Validators.required]],
    [this.emailField.key]: ['', [Validators.required, Validators.email]],
    [this.userRoleField.key]: ['', [Validators.required]],
    [this.availableLabsField.key]: [],
  });

  ngOnInit(): void {
    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser = user;
      });
    this.setAvailableUserRoles(this.currentUser.role);
    this.laboratory = this.authenticationService.selectedLaboratory;
    this.orgId = this.authenticationService.currentOrganisation;

    if (this.currentUser.organisationId === this.orgId) {
      this.availableLabs.set(this.currentUser.laboratories || []);
      this.setAvailableLabs(true);
    } else {
      this.labsLoading.set(true);
      this.getOrganisation();
    }

    const unsavedCachedData = this.userService.retrieveAndClearUnsavedUserCache;
    if (unsavedCachedData) {
      this.updateFormValues(unsavedCachedData);
    }
  }

  getOrganisation(): void {
    this.organisationService
      .getOrganisation(this.orgId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: OrganisationIndexView) => {
          this.availableLabs.set(response.labSummary.filter((lab) => !lab.isDeleted) || []);
          this.setAvailableLabs(false);
          this.labsLoading.set(false);
        },
        error: (err: ErrorObject) => {
          this.labsLoading.set(false);
          this.error.set(err);
          this.openModal('error');
        },
      });
  }

  setAvailableUserRoles(role: Role): void {
    if (OGI_ADMIN_ROLE_LIST.includes(role)) {
      this.userRoleField.options.push({
        key: Role.OGI_Laboratory_Administrator,
        value: 'OGI Laboratory Administrator',
      });
    }

    if (role === Role.OGI_Super_Administrator) {
      this.userRoleField.options.push(
        { key: Role.OGI_Super_Administrator, value: 'OGI Super Administrator' },
        { key: Role.Super_User, value: 'Super User' },
      );
    }
  }

  setAvailableLabs(ownOrg: boolean): void {
    this.availableLabsField.options = (
      ownOrg ? this.currentUser.laboratories : this.availableLabs()
    ).map((lab) => ({ key: lab.laboratoryId, value: lab.laboratoryName }));

    this.addUserForm
      .get('lab')
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((labId) => {
        const lab = this.availableLabs().find(
          (l) => l.laboratoryId === parseInt(labId),
        );
        if (lab && !this.selectedLabs().some((l) => l.laboratoryId === lab.laboratoryId)) {
          this.selectedLabs.update((labs) => [...labs, lab]);
          this.availableLabsField.options = this.availableLabsField.options.filter(
            (option) => option.key !== lab.laboratoryId,
          );
          this.addUserForm.get('lab').setValue(null, { emitEvent: false });
        }
      });
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'add-user-button':
        this.closeModal('create-account');
        break;
      case 'error-button':
        this.closeModal('error');
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
    }
  }

  removeLab(lab: Laboratory): void {
    this.selectedLabs.update((labs) =>
      labs.filter((l) => l.laboratoryId !== lab.laboratoryId),
    );
    this.availableLabsField.options = [
      ...this.availableLabsField.options,
      { key: lab.laboratoryId, value: lab.laboratoryName },
    ].sort((a, b) => (a.value as string).localeCompare(b.value as string));
    this.addUserForm.get('lab').setValue(null, { emitEvent: false });
  }

  resetForm(): void {
    this.addUserForm.reset();
    this.addUserForm.markAsPristine();
    this.selectedLabs.set([]);
    this.availableLabsField.options = this.availableLabs().map((lab) => ({
      key: lab.laboratoryId,
      value: lab.laboratoryName,
    }));
  }

  get f(): FormGroup['controls'] {
    return this.addUserForm.controls;
  }

  onSubmit(): void {
    if (this.isProcessing()) return;

    this.error.set(null);
    this.labSelectError.set(null);
    this.addUserSuccessMessage.set(null);

    if (
      this.selectedLabs().length < 1 &&
      !SUPER_ADMIN_ROLE_LIST.includes(this.f.role.value as Role)
    ) {
      this.labSelectError.set('Please select at least one lab');
      return;
    }

    const labIds = this.selectedLabs().map((lab) => lab.laboratoryId);

    const user: UserCreate = {
      firstName: this.f.firstName.value as string,
      lastName: this.f.lastName.value as string,
      email: this.f.email.value as string,
      userType: this.f.role.value as Role,
      laboratoryIds: labIds,
    };

    this.isProcessing.set(true);
    this.userService.inviteUser(user).subscribe({
      next: (response: string) => {
        this.addUserSuccessMessage.set(response);
        this.isProcessing.set(false);
        this.resetForm();
        this.openModal('create-account');
      },
      error: (err: ErrorObject) => {
        this.isProcessing.set(false);
        this.error.set(err);
        this.openModal('error');
      },
    });
  }

  updateFormValues(user: UserCreate): void {
    this.addUserForm.patchValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.userType,
    });

    this.selectedLabs.set(
      this.availableLabs().filter((lab) => user.laboratoryIds.includes(lab.laboratoryId)),
    );

    this.addUserForm.markAsDirty();
  }

  getCurrentFormValues(): UserCreate {
    return {
      firstName: this.f.firstName.value as string,
      lastName: this.f.lastName.value as string,
      email: this.f.email.value as string,
      userType: this.f.role.value as Role,
      laboratoryIds: this.selectedLabs().map((lab) => lab.laboratoryId),
    };
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  ngOnDestroy(): void {
    if (
      this.authEventService.isUnauthorizedNavigationPending &&
      !this.addUserForm.pristine
    ) {
      this.userService.onUpdateCreateUserCache(this.getCurrentFormValues());
    }

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (
        this.addUserForm.pristine ||
        !this.currentUser ||
        this.authEventService.isUnauthorizedNavigationPending
      ) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation.subscribe((isConfirmed) => resolve(isConfirmed));
      }
    });
  }
}
